import { type JSX, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { FeedbackFloater } from '@/components/FeedbackFloater';
import { setActionBarContainer } from '@/lib/actionBarPortal';

import {
  ActionBarSlotFactory,
  ContentFactory,
  GridFactory,
  SidebarFactory,
  TopbarFactory,
  WelcomeFactory,
} from './AppLayout.factory';

import './AppLayout.scss';

const DESKTOP_QUERY = '(min-width: 769px)';

type AppLayoutProps = {
  children: ReactNode;
};

export function AppLayout({ children }: AppLayoutProps): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches
  );

  const handleToggle = (): void => setIsOpen((prev) => !prev);

  const actionBarRef = useRef<HTMLDivElement>(null);

  // Register the portal target before any child effects run so ActionBar
  // components can access it synchronously via useSyncExternalStore.
  useLayoutEffect(() => {
    setActionBarContainer(actionBarRef.current);
    return () => setActionBarContainer(null);
  }, []);

  // Track the action bar slot height via ResizeObserver and write it directly
  // to the grid as --action-bar-height. ContentFactory reads this var in its
  // recipe to pad its bottom edge — no React state, no re-render on resize.
  useEffect(() => {
    const slot = actionBarRef.current;
    if (slot === null || typeof ResizeObserver === 'undefined') return;

    const grid = slot.parentElement;
    if (grid === null) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) {
        grid.style.setProperty('--action-bar-height', `${entry.contentRect.height}px`);
      }
    });

    observer.observe(slot);

    return () => {
      observer.disconnect();
      grid.style.removeProperty('--action-bar-height');
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mql = window.matchMedia(DESKTOP_QUERY);
    const handleChange = (e: MediaQueryListEvent): void => setIsOpen(e.matches);

    mql.addEventListener('change', handleChange);

    return (): void => mql.removeEventListener('change', handleChange);
  }, []);

  return (
    <GridFactory
      className='grid'
      data-open={String(isOpen)}>
      <TopbarFactory
        className='topbar'
        onToggle={handleToggle}
        isMenuOpen={isOpen}
      />

      {isOpen && <WelcomeFactory className='welcome' />}

      <SidebarFactory
        className='sidebar'
        isOpen={isOpen}
      />

      <ContentFactory className='content'>{children}</ContentFactory>

      {/* Portal target for the sticky action bar. Outside <main>, overlays
          the content column at the bottom via grid alignment. */}
      <ActionBarSlotFactory
        ref={actionBarRef}
        className='action-bar-slot'
      />

      <FeedbackFloater />
    </GridFactory>
  );
}
